import { useState, useEffect } from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { trpc } from '../../lib/trpc.js';
import { getStoredUser } from '../../lib/auth.js';

interface RoasOverview {
  revenue: number;
  spend: number;
  roas: number;
  calls: number;
  rpc?: number;
  cpc?: number;
  date?: string;
}

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

function getRoasColor(roas: number) {
  if (roas >= 2.5) return '#10b981';
  if (roas >= 1.5) return '#f59e0b';
  return '#ef4444';
}

function KPICard({
  label,
  value,
  loading,
  format,
}: {
  label: string;
  value: number;
  loading: boolean;
  format: 'currency' | 'number' | 'multiplier';
}) {
  const formatted = format === 'currency' ? formatCurrency(value)
    : format === 'multiplier' ? `${value.toFixed(2)}x`
    : value.toLocaleString();

  if (loading) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 animate-pulse">
        <div className="h-3 bg-slate-800 rounded w-16 mb-3" />
        <div className="h-8 bg-slate-800 rounded w-24 mb-2" />
        <div className="h-3 bg-slate-800 rounded w-20" />
      </div>
    );
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
      <div className="text-sm text-slate-400 mb-1">{label}</div>
      <div className="text-2xl font-bold text-white">{formatted}</div>
      <div className="text-xs text-slate-500 mt-1">Today</div>
    </div>
  );
}

export default function PulsePage() {
  const [overview, setOverview] = useState<RoasOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.roas.overview.query({ tenantId: user.tenantId })
      .then(data => setOverview(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Static hourly ROAS pattern for display context (shape only, no real values)
  const hourlyPattern = Array.from({ length: 24 }, (_, i) => ({
    hour: `${i}:00`,
    roas: i >= 6 && i <= 22 ? 1.5 + (i >= 9 && i <= 15 ? 1.5 : 0.5) : 0.5,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">🏠 Pulse</h1>
        <div className="text-sm text-slate-400">Today</div>
      </div>

      {/* KPI Cards — real data */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Revenue" value={overview?.revenue ?? 0} loading={loading} format="currency" />
        <KPICard label="Spend" value={overview?.spend ?? 0} loading={loading} format="currency" />
        <KPICard label="ROAS" value={overview?.roas ?? 0} loading={loading} format="multiplier" />
        <KPICard label="Calls" value={overview?.calls ?? 0} loading={loading} format="number" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ROAS by Hour — coming soon (no hourly breakdown API yet) */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-1">ROAS by Hour</h3>
          <p className="text-xs text-slate-500 mb-4">Typical pattern — real hourly data coming soon</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={hourlyPattern}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="hour" stroke="#64748b" tick={{ fontSize: 10 }} interval={3} />
              <YAxis stroke="#64748b" tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              <Bar dataKey="roas" radius={[2, 2, 0, 0]}>
                {hourlyPattern.map((entry, index) => (
                  <Cell key={index} fill={getRoasColor(entry.roas)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Additional KPIs if available */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4">Performance Summary</h3>
          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-10 bg-slate-800 rounded-lg" />)}
            </div>
          ) : (
            <div className="space-y-3">
              {[
                { label: 'Revenue per Call', value: overview?.rpc != null ? formatCurrency(overview.rpc) : '—' },
                { label: 'Cost per Click', value: overview?.cpc != null ? `$${overview.cpc.toFixed(2)}` : '—' },
                { label: 'Total Spend', value: formatCurrency(overview?.spend ?? 0) },
                { label: 'Total Calls', value: (overview?.calls ?? 0).toLocaleString() },
              ].map(stat => (
                <div key={stat.label} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <span className="text-sm text-slate-400">{stat.label}</span>
                  <span className="text-sm font-semibold text-white">{stat.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Predictions — coming soon */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h3 className="text-lg font-semibold text-white mb-4">Predictions</h3>
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <span className="text-4xl">🔮</span>
          <p className="text-slate-300 font-medium text-sm">Coming soon</p>
          <p className="text-slate-500 text-xs text-center max-w-xs">
            AI-powered revenue predictions require at least 14 days of historical data.
          </p>
        </div>
      </div>

      {/* AI Briefing — coming soon */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <h3 className="text-lg font-semibold text-white mb-3">🤖 AI Briefing</h3>
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <span className="text-4xl">🤖</span>
          <p className="text-slate-300 font-medium text-sm">Coming soon</p>
          <p className="text-slate-500 text-xs text-center max-w-sm">
            Daily AI briefings summarizing performance trends and recommendations will be available once enough data is collected.
          </p>
        </div>
      </div>
    </div>
  );
}
